import React, { useMemo, useState } from "react";
import axios from "axios";

const api = axios.create({ withCredentials: true });

export default function BlockedIpsPanel({ rows = [], onRefresh }) {
  const [ip, setIp] = useState("");
  const [reason, setReason] = useState("manual_admin_block");
  const [busy, setBusy] = useState(false);
  const [sort, setSort] = useState({ key: "created_at", dir: "desc" });

  const sortedRows = useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => compareValues(a, b, sort.key, sort.dir));
    return copy;
  }, [rows, sort]);

  const addIp = async () => {
    if (!ip.trim()) return;
    setBusy(true);
    try {
      await api.post("/api/admin/security/bans", { ip: ip.trim(), reason });
      setIp("");
      onRefresh?.();
    } finally {
      setBusy(false);
    }
  };

  const removeIp = async (targetIp) => {
    setBusy(true);
    try {
      await api.delete("/api/admin/security/bans", { data: { ip: targetIp } });
      onRefresh?.();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div style={styles.topRow}>
        <h2 style={styles.title}>Blocked IPs</h2>
      </div>

      <div style={styles.formRow}>
        <input
          style={styles.input}
          value={ip}
          onChange={(e) => setIp(e.target.value)}
          placeholder="Enter IP to block"
        />
        <input
          style={styles.input}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Reason"
        />
        <button style={styles.primaryBtn} type="button" disabled={busy} onClick={addIp}>
          Add blocked IP
        </button>
      </div>

      <div style={styles.tableWrap}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>{sortHeader("IP", "ip", sort, setSort)}</th>
              <th style={styles.th}>{sortHeader("Reason", "reason", sort, setSort)}</th>
              <th style={styles.th}>{sortHeader("Created", "created_at", sort, setSort)}</th>
              <th style={styles.th}>Action</th>
            </tr>
          </thead>
          <tbody>
            {sortedRows.length ? sortedRows.map((row, idx) => (
              <tr key={`${row.ip || "ip"}-${idx}`}>
                <td style={styles.td}>{row.ip || row.value || "-"}</td>
                <td style={styles.td}>{row.reason || "-"}</td>
                <td style={styles.td}>{formatDate(row.created_at || row.createdAt)}</td>
                <td style={styles.td}>
                  <button type="button" style={styles.removeBtn} disabled={busy} onClick={() => removeIp(row.ip || row.value)}>
                    Remove
                  </button>
                </td>
              </tr>
            )) : (
              <tr><td style={styles.empty} colSpan={4}>No blocked IPs yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function compareValues(a, b, key, dir) {
  const mult = dir === "asc" ? 1 : -1;
  if (key === "ip") return mult * compareIp(a.ip || a.value, b.ip || b.value);
  if (key === "created_at") return mult * (toTime(a.created_at || a.createdAt) - toTime(b.created_at || b.createdAt));
  return mult * String(a.reason || "").localeCompare(String(b.reason || ""), undefined, { sensitivity: "base", numeric: true });
}

function compareIp(a, b) {
  const pa = String(a || "").split(".").map((x) => Number(x) || 0);
  const pb = String(b || "").split(".").map((x) => Number(x) || 0);
  for (let i = 0; i < Math.max(pa.length, pb.length); i += 1) {
    const diff = (pa[i] || 0) - (pb[i] || 0);
    if (diff) return diff;
  }
  return 0;
}

function toTime(value) {
  const d = new Date(value || 0);
  return Number.isNaN(d.getTime()) ? 0 : d.getTime();
}

function formatDate(value) {
  if (!value) return "-";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString();
}

function sortHeader(label, key, sort, setSort) {
  const active = sort.key === key;
  const arrow = active ? (sort.dir === "asc" ? "▲" : "▼") : "↕";
  return (
    <button type="button" onClick={() => setSort((prev) => ({ key, dir: prev.key === key && prev.dir === "asc" ? "desc" : "asc" }))} style={styles.sortBtn}>
      {label} <span style={styles.arrow}>{arrow}</span>
    </button>
  );
}

const styles = {
  topRow: { display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", marginBottom: 14 },
  title: { margin: 0, fontSize: 18, fontWeight: 800 },
  sortBtn: { background: "transparent", border: "none", padding: 0, fontWeight: 800, cursor: "pointer", color: "#111" },
  arrow: { fontSize: 11, marginLeft: 4, color: "#111" },
  formRow: { display: "grid", gridTemplateColumns: "1.2fr 1fr auto", gap: 12, marginBottom: 16 },
  input: { width: "100%", border: "1px solid #d0d5dd", borderRadius: 12, padding: "12px 14px", fontSize: 14 },
  primaryBtn: { background: "#0d5d6c", color: "#fff", border: "none", borderRadius: 12, padding: "12px 14px", fontWeight: 800, cursor: "pointer" },
  removeBtn: { background: "#b42318", color: "#fff", border: "none", borderRadius: 10, padding: "8px 12px", fontWeight: 800, cursor: "pointer" },
  tableWrap: { overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse" },
  th: { textAlign: "left", padding: "14px 10px", borderBottom: "1px solid #e5e7eb", fontWeight: 800, whiteSpace: "nowrap", color: "#111" },
  td: { padding: "14px 10px", borderBottom: "1px solid #f0f0f0", verticalAlign: "top" },
  empty: { padding: 20, textAlign: "center", color: "#667085" },
};
